/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 *
 * @file OnboardingSplashPage.jsx
 * Parent-driven onboarding splash screen.
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import OnboardingGetStartedPanel from './OnboardingGetStartedPanel';
import OnboardingPromotionPanel from './OnboardingPromotionPanel';
import { OnboardingSplashStylesRoot, classes } from './styles/OnboardingSplashStyles';

const OnboardingSplashPage = ({
  className,
  content,
  onActionClick,
  onLearnMoreClick,
  onPreviewQuestionsClick
}) => {
  const { getStarted, promotion } = content || {};

  return (
    <OnboardingSplashStylesRoot className={`${classes.shell} ${className}`}>
      <div className={classes.main}>
        <Typography component="h2" className={classes.pageTitle}>
          {getStarted.title}
        </Typography>

        <div className={classes.contentRow}>
          <OnboardingGetStartedPanel
            actionCards={getStarted.actionCards}
            onActionClick={onActionClick}
            previewQuestions={getStarted.previewQuestions}
            onPreviewQuestionsClick={onPreviewQuestionsClick}
          />
          <OnboardingPromotionPanel
            title={promotion.title}
            features={promotion.features}
            learnMoreLabel={promotion.learnMoreLabel}
            onLearnMoreClick={onLearnMoreClick}
            learnMoreDataAttributes={promotion.learnMoreDataAttributes}
          />
        </div>
      </div>
    </OnboardingSplashStylesRoot>
  );
};

OnboardingSplashPage.propTypes = {
  className: PropTypes.string,
  content: PropTypes.shape({
    getStarted: PropTypes.shape({
      title: PropTypes.string.isRequired,
      actionCards: PropTypes.arrayOf(PropTypes.object).isRequired,
      previewQuestions: PropTypes.shape({
        promptLabel: PropTypes.string,
        linkLabel: PropTypes.string.isRequired
      })
    }).isRequired,
    promotion: PropTypes.shape({
      title: PropTypes.string.isRequired,
      features: PropTypes.arrayOf(PropTypes.string).isRequired,
      learnMoreLabel: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  onActionClick: PropTypes.func,
  onLearnMoreClick: PropTypes.func,
  onPreviewQuestionsClick: PropTypes.func
};

OnboardingSplashPage.defaultProps = {
  className: '',
  onActionClick: () => {},
  onLearnMoreClick: () => {},
  onPreviewQuestionsClick: () => {}
};

export default OnboardingSplashPage;
